import axios from 'Src/helpers/api/axiosInstance';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { DEFAULT_SKIP } from '../../../consts/table';
import { UserProfile } from '../../../models/profile';
import { clearSessions } from '../../session/session';
import { Sport } from '../../../models/sport';
import { SchoolForm } from '../../../models/form';
import { SchoolHouse } from '../../../models/house';
/** CONST */
export const DEFAULT_FILTER = {
  limit: 10,
  skip: DEFAULT_SKIP
};

let rolesRequestCache: { key: string; expiresAt: number; promise: Promise<any> } | undefined;

/** USER */
export function getRoles(user: AppUser) {
  const key = user.loginSessionKey;

  // App and role-aware layouts mount together and historically issued the
  // same relatively expensive request twice. Share concurrent/nearby reads;
  // the short lifetime still lets a newly granted role appear promptly.
  if (rolesRequestCache && rolesRequestCache.key === key && rolesRequestCache.expiresAt > Date.now()) {
    return rolesRequestCache.promise;
  }

  const config = {
    headers: { usid: key }
  };

  const promise = axios.get(`${window.apiBase}/i/roles`, config).then(
    response => {
      return response.data;
    },
    err => {
      const status = propz.get(err, ['response', 'status']);
      if (status === 401) {
        clearSessions();
        //fix it later
        document.location.reload();
      } else {
        rolesRequestCache = undefined;
        throw err;
      }
    }
  );
  rolesRequestCache = { key, expiresAt: Date.now() + 5000, promise };
  return promise;
}

export function getProfile(user: AppUser) {
  const key = user.loginSessionKey;

  const config = {
    headers: { usid: key }
  };

  return axios.get(`${window.apiBase}/i/profile`, config).then(response => {
    return response.data;
  });
}

export function setProfile(user: AppUser, profile: UserProfile) {
  const key = user.loginSessionKey;

  const config = {
    headers: { usid: key }
  };

  return axios.put(`${window.apiBase}/i/profile`, profile, config).then(response => {
    return response.data;
  });
}

export function deleteUser(user: AppUser, email: string, password: string) {
  const key = user.loginSessionKey;

  const config = {
    headers: { usid: key }
  };

  return axios.post(`${window.apiBase}/i/profile/wipe`, { email, password }, config).then(response => {
    if (response.data && response.data.isError) {
      return Promise.reject(response.data);
    }
    return response.data;
  });
}

export function getSchool(user: AppUser, schoolId: string, optKey?: string) {
  const key = user.roleSessionKey;

  let config;
  if (typeof optKey !== 'undefined') {
    config = {
      headers: { usid: optKey }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${schoolId}`, config).then(
    response => {
      return response.data;
    },
    err => {
      const status = propz.get(err, ['response', 'status']);

      if (status === 401) {
        clearSessions();
        //fix it later
        document.location.reload();
      } else {
        throw err;
      }
    }
  );
}

export function getSchools(user: AppUser, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools`, config).then(response => {
    return response.data;
  });
}

const pendingTurnstileTokens: { [widgetId: string]: Promise<string> | undefined } = {};

function getInvisibleTurnstileToken(widgetId: string): Promise<string> {
  const pending = pendingTurnstileTokens[widgetId];
  if (pending) return pending;

  const tokenPromise = new Promise<string>((resolve, reject) => {
    const key = widgetId.replace(/-/g, '_');
    const resolveKey = `__turnstileResolveFn_${key}`;
    const renderedId = (window as any)[`__turnstileWidgetId_${key}`];

    if (!(window as any).turnstile || renderedId === undefined) {
      reject(new Error('Security verification is not ready. Please refresh the page and try again.'));
      return;
    }

    let timeoutId: number;
    const onToken = (token: string) => {
      window.clearTimeout(timeoutId);
      if ((window as any)[resolveKey] === onToken) delete (window as any)[resolveKey];
      if (token) resolve(token);
      else reject(new Error('Security verification returned an empty token. Please try again.'));
    };

    timeoutId = window.setTimeout(() => {
      if ((window as any)[resolveKey] === onToken) delete (window as any)[resolveKey];
      reject(new Error('Security verification timed out. Please try again.'));
    }, 15000);

    (window as any)[resolveKey] = onToken;

    try {
      (window as any).turnstile.reset(renderedId);
      (window as any).turnstile.execute(renderedId);
    } catch (error) {
      window.clearTimeout(timeoutId);
      if ((window as any)[resolveKey] === onToken) delete (window as any)[resolveKey];
      reject(error);
    }
  });

  pendingTurnstileTokens[widgetId] = tokenPromise;
  tokenPromise.then(
    () => delete pendingTurnstileTokens[widgetId],
    () => delete pendingTurnstileTokens[widgetId]
  );
  return tokenPromise;
}

/** FORMS AND HOUSES */
export function getSchoolForms(user: AppUser, filter = DEFAULT_FILTER): Promise<SchoolForm[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get<SchoolForm[]>(`${window.apiBase}/i/schools/${activeSchoolId}/forms`, config).then(response => {
    return response.data;
  });
}

export function getSchoolHouses(user: AppUser, filter = DEFAULT_FILTER): Promise<SchoolHouse[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get<SchoolHouse[]>(`${window.apiBase}/i/schools/${activeSchoolId}/houses`, config).then(response => {
    return response.data;
  });
}

export function getSchoolSports(user: AppUser, filter = DEFAULT_FILTER): Promise<Sport[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get<Sport[]>(`${window.apiBase}/i/schools/${activeSchoolId}/sports`, config).then(response => {
    return response.data;
  });
}

export function getSchoolClubSports(user: AppUser, filter = DEFAULT_FILTER): Promise<Sport[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get<Sport[]>(`${window.apiBase}/i/schools/${activeSchoolId}/clubSports`, config).then(response => {
    return response.data;
  });
}

export function getSchoolVenues(user: AppUser, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/places`, config).then(response => {
    return response.data;
  });
}

export function getPlacesAndPostcodes(user: AppUser, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/placesAndPostcodes`, config).then(response => {
    return response.data;
  });
}

export function changePhone(user: AppUser | { loginSessionKey: string }, phone: string, captchaToken?: string) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.put(`${window.apiBase}/i/profile/phone`, { phone, captchaToken }, config).then(response => {
    return response.data;
  });
}

export async function resendPhoneCode(user: AppUser | { loginSessionKey: string }) {
  const { loginSessionKey: key } = user;

  const captchaToken = await getInvisibleTurnstileToken('turnstile-resend-phone');

  const config = { headers: { usid: key } };

  return axios.post(`${window.apiBase}/i/profile/phone/resend`, { captchaToken }, config).then(response => {
    const payload = response.data;
    if (payload?.isError) return Promise.reject({ response: { data: payload } });
    return payload;
  });
}

export function changeEmail(user: AppUser | { loginSessionKey: string }, email: string, captchaToken?: string) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.put(`${window.apiBase}/i/profile/email`, { email, captchaToken }, config).then(response => {
    return response.data;
  });
}

export async function resendEmailCode(user: AppUser | { loginSessionKey: string }) {
  const { loginSessionKey: key } = user;

  const captchaToken = await getInvisibleTurnstileToken('turnstile-resend-email');

  const config = { headers: { usid: key } };

  return axios.post(`${window.apiBase}/i/profile/email/resend`, { captchaToken }, config).then(response => {
    const payload = response.data;
    if (payload?.isError) return Promise.reject({ response: { data: payload } });
    return payload;
  });
}

export function getPermissions(user: AppUser, filter = DEFAULT_FILTER) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/permissions`, config).then(response => {
    return response.data;
  });
}

export function deletePermission(user: AppUser, permissionId: string) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.delete(`${window.apiBase}/i/permissions/${permissionId}`, config).then(response => {
    return response.data;
  });
}

export function getProfileRequests(user: AppUser, filter = DEFAULT_FILTER) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };
  return axios.get(`${window.apiBase}/i/permissions/requests`, config).then(response => {
    return response.data;
  });
}

export function createProfileRequest(
  user: AppUser | { loginSessionKey: string },
  data: { preset: string; schoolId: string; comment?: string; details?: any; trialModules?: string[] }
) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.post(`${window.apiBase}/i/permissions/requests`, data, config).then(response => {
    return response.data;
  });
}

export function deleteProfileRequest(user: AppUser, requestId: string) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key }
  };
  return axios.delete(`${window.apiBase}/i/permissions/requests/${requestId}`, config).then(response => {
    return response.data;
  });
}

export function getNotificationsChannels(user: AppUser) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.get(`${window.apiBase}/i/notifications/channels`, config).then(response => {
    return response.data;
  });
}

export function createNotificationChannel(user: AppUser, data: { token: string; type: string }) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.post(`${window.apiBase}/i/notifications/channels`, data, config).then(response => {
    return response.data;
  });
}

export function deleteNotificationChannel(user: AppUser, notificationChannelId: string) {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.delete(`${window.apiBase}/i/notifications/channels/${notificationChannelId}`, config).then(response => {
    return response.data;
  });
}

export function parentAutoLink(
  user: { loginSessionKey: string },
  schoolId: string
): Promise<{ linkedCount: number; linkedStudents: Array<{ studentId: string; studentName: string }> }> {
  const { loginSessionKey: key } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/permissions/requests/parent/auto-link`, { schoolId }, config)
    .then(response => response.data);
}
